/**
 * 「날씨 위치」 한 줄의 **글자**를 한 곳에 모은다 (2026-09-21 · 시안 v2 승인).
 *
 * 두 화면(회사정보 · 사업장 상세)이 **다른 부품**으로 그린다 — 회사정보는 공용 `Badge`, 사업장은 인사 화면의
 * 테두리형 꼬리표(`hre-badge`). 사용자 결정 「그대로」라 부품을 통일하지 않는다. 그래서 **모양은 각자**,
 * **말은 여기 하나**로 둔다(같은 뜻이 두 글자로 갈라지지 않게).
 *
 * ★ 위치는 주소에서 셈한 값이라 고칠 수 없다 — 고칠 것은 **주소**다. 그래서 어떤 상태에도 「위치 고치기」가 없다.
 */
import type { WeatherLocation } from '../api/weather';

export type WeatherLocationTone = 'ok' | 'warn' | 'muted';

export interface WeatherLocationCopy {
  tone: WeatherLocationTone;
  /** 꼬리표 글자 — 「주소로 찾음」 · 「위치를 알 수 없음」 · 「주소 없음」. */
  badge: string;
  /** 굵게 보여줄 한 줄. 없으면 null(꼬리표만). */
  line: string | null;
  /** 밑에 붙는 설명 — 무엇을 해야 하는지. */
  hint: string;
}

export function weatherLocationCopy(loc: WeatherLocation | null | undefined, place: 'company' | 'worksite' = 'company'): WeatherLocationCopy {
  const what = place === 'company' ? '회사' : '이 사업장';
  if (!loc) {
    return { tone: 'muted', badge: '확인 중', line: null, hint: '날씨 위치를 불러오는 중입니다.' };
  }
  if (loc.kind === 'ok') {
    return {
      tone: 'ok',
      badge: '주소로 찾음',
      line: loc.regionName,
      hint: loc.level === 'dong'
        ? `${what} 주소로 날씨를 가져옵니다. 주소를 고치면 위치도 따라갑니다.`
        : `동 이름이 없어 «구·군»까지 맞췄습니다. ${what} 주소로 날씨를 가져옵니다.`,
    };
  }
  if (loc.kind === 'no_address') {
    return {
      tone: 'muted',
      badge: '주소 없음',
      line: null,
      hint: '주소를 적으면 그 자리의 날씨가 나옵니다 — 「주소 찾기」로 고르면 형식이 맞습니다.',
    };
  }
  return {
    tone: 'warn',
    badge: '위치를 알 수 없음',
    line: loc.regionName,
    hint: loc.regionName
      // 시도까지만 맞은 경우 — 도 전체를 대표하는 한 점이라 그 날씨를 보여주면 틀린 값이 된다.
      ? `「${loc.regionName}」까지만 알아냈습니다 — 시·군·구와 동 이름까지 적어 주십시오.`
      : '주소에서 시·군·구를 찾지 못했습니다 — 행정 주소로 적어 주십시오.',
  };
}
